import React from "react"
import {useTranslation} from "react-i18next"
import {InformationCircleIcon, MagnifyingGlassIcon, XMarkIcon} from "@heroicons/react/24/outline"
import {parseISO} from "date-fns"

import {InputBase} from "../../../components/fields/Input.tsx"
import {Tooltip, TTooltipButtonProps} from "../../../components/Tooltip.tsx"
import {useKlaraUpdateCenterQuery} from "../../../queries/klaraDashboard.ts"
import {useDebouncedValue} from "../../../utils/hooks.tsx"
import {EMPTY_ARRAY} from "../../../utils/index.ts"
import {KlaraFiltersContext} from "../filters.tsx"
import {clampDayToRange, getPeriodRange} from "../period.ts"
import {TKlaraFeedEntry} from "../sessions.ts"
import {dashboardDay, useDashboardClock} from "../useDashboardClock.ts"
import {useKlaraPeriod} from "../useKlaraPeriod.ts"
import {CardState} from "./CardState.tsx"
import {Drawer} from "./Drawer.tsx"
import {formatDate} from "./formatDate.ts"
import {usePeriodLabel} from "./periodLabel.ts"
import {SessionList, TSessionListJump} from "./SessionList.tsx"

type TProps = {
  /** Session to scroll to when the drawer opens. */
  entryId?: number
  onClose: () => void
}

/** Mounted only while open, so every opening starts with an empty search and the latest updates. */
export const UpdateCenterDrawer: React.FC<TProps> = ({entryId, onClose}) => {
  const {t} = useTranslation()
  const {salesPersonExternalId, scopeVersion} = KlaraFiltersContext.useContext()
  const [period] = useKlaraPeriod()
  // The picker computes preset dates on the browser's calendar.
  const range = React.useMemo(() => getPeriodRange(period), [period])
  const periodLabel = usePeriodLabel(period)
  // A lifetime jump can reach the dashboard's today, which may differ from the browser's.
  const today = dashboardDay(useDashboardClock())
  const [search, setSearch] = React.useState("")
  const debouncedSearch = useDebouncedValue(search)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null)
  const searchRef = React.useRef<HTMLInputElement>(null)
  const backToLatestRef = React.useRef<HTMLButtonElement>(null)

  // The selection only applies to the feed it was picked from: a new scope, a search or a jump drops it. It is
  // scrolled into view once per opening; the flag lives here so it survives the list unmounting behind an error.
  const [selection, setSelection] = React.useState({entryId, scopeVersion, isScrolled: false})
  const selectedEntryId = selection.scopeVersion === scopeVersion ? selection.entryId : undefined
  const handleScrolledToSelected = React.useCallback(
    () => setSelection(current => ({...current, isScrolled: true})),
    []
  )

  // A jump moves the feed's upper bound within the period; like the selection it belongs to the scope it was made in.
  const [jump, setJump] = React.useState<{to: string; scopeVersion: number} | null>(null)
  const jumpTo = jump?.scopeVersion === scopeVersion ? jump.to : undefined

  // Applying or dropping a jump removes the control that did it, so focus moves to one that stays: the way back
  // after a jump, the search after returning to the latest updates.
  const focusAfterJump = React.useRef<"back to latest" | "search" | null>(null)
  React.useEffect(() => {
    const target = focusAfterJump.current === "back to latest" ? backToLatestRef : searchRef
    if (focusAfterJump.current) {
      focusAfterJump.current = null
      target.current?.focus()
    }
  }, [jump])

  const {data, hasNextPage, isFetchingNextPage, fetchNextPage, isPending, isError, isFetching, refetch} =
    useKlaraUpdateCenterQuery({
      search: debouncedSearch,
      salesPersonExternalId,
      from: range.from,
      to: jumpTo ?? range.to,
    })

  const entries = React.useMemo(
    () => data?.pages.flatMap<TKlaraFeedEntry>(page => page.data.update_center_entries) ?? EMPTY_ARRAY,
    [data?.pages]
  )
  const totalEventCount = data?.pages[0]?.data.meta.total_event_count

  const scrollToTop = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0
    }
  }

  const firstScopeVersion = React.useRef(scopeVersion)
  React.useEffect(() => {
    if (scopeVersion !== firstScopeVersion.current) {
      scrollToTop()
    }
  }, [scopeVersion])

  const changeJump = (to: string | undefined) => {
    focusAfterJump.current = to === undefined ? "search" : "back to latest"
    setJump(to === undefined ? null : {to, scopeVersion})
    setSelection({entryId: undefined, scopeVersion, isScrolled: false})
    scrollToTop()
  }

  const jumpBounds: TSessionListJump = {
    min: range.from,
    max: clampDayToRange(today, range, today),
    onJump: date => changeJump(clampDayToRange(date, range, today)),
  }

  const handleFetchNextPage = React.useCallback(() => void fetchNextPage(), [fetchNextPage])

  return (
    <Drawer
      onClose={onClose}
      title={
        <>
          {t("KlaraDashboard_UpdateCenter_Title")}
          <span className={"mt-0.5 block text-[11.5px] font-normal tracking-normal text-cr-slate-60"}>
            {totalEventCount !== undefined ? (
              <>
                <span className={"sr-only"}> · </span>
                {t("KlaraDashboard_UpdateCenter_Updates", {count: totalEventCount})}
                {" · "}
              </>
            ) : (
              <span className={"sr-only"}> · </span>
            )}
            {periodLabel}
          </span>
        </>
      }
      titleAside={
        <Tooltip Button={TotalInfoButton} openOnFocus closeOnEscape>
          <div role={"tooltip"} className={"max-w-xs rounded-lg bg-cr-black px-4 py-2 text-sm text-cr-white"}>
            {t("KlaraDashboard_UpdateCenter_TotalInfo")}
          </div>
        </Tooltip>
      }
      headerContent={
        <>
          <InputBase
            ref={searchRef}
            type={"search"}
            aria-label={t("KlaraDashboard_UpdateCenter_Search")}
            placeholder={t("SearchPlaceholder")}
            Icon={MagnifyingGlassIcon}
            // The shared `.input-border > input` text size outranks plain utilities on the input, hence the overrides.
            borderClassName={
              "rounded-[7px] ring-cr-grey-15 [&_svg]:size-3.5 [&_svg]:text-cr-slate-40 [&>input]:py-[7px]! [&>input]:pl-2! [&>input]:text-xs! [&>input]:leading-[18px]!"
            }
            inputClassName={"placeholder:text-cr-slate-40"}
            value={search}
            onChange={event => {
              setSearch(event.target.value)
              setSelection({entryId: undefined, scopeVersion, isScrolled: false})
            }}
          />
          {jumpTo !== undefined && (
            <div
              role={"status"}
              className={
                "flex items-center gap-1.5 self-start rounded-full bg-cr-blue-light px-2.5 py-1 text-[11.5px] font-semibold text-cr-blue-dark"
              }
            >
              <span>
                {t("KlaraDashboard_UpdateCenter_ShowingUpTo", {
                  date: formatDate(parseISO(jumpTo).getTime(), "d MMM yyyy"),
                })}
              </span>
              <button
                ref={backToLatestRef}
                type={"button"}
                onClick={() => changeJump(undefined)}
                className={"inline-flex items-center gap-1 hover:underline"}
              >
                <span aria-hidden={"true"}>·</span>
                {t("KlaraDashboard_UpdateCenter_BackToLatest")}
                <XMarkIcon className={"size-3.5"} aria-hidden={"true"} />
              </button>
            </div>
          )}
        </>
      }
    >
      <div ref={scrollContainerRef} className={"scrollbar-slim grow overflow-y-auto px-6 pt-4 pb-3"}>
        <CardState
          query={{isPending, isError, isFetching: isFetching && !isFetchingNextPage, refetch}}
          skeletonClassName={"h-64"}
        >
          {entries.length === 0 ? (
            <p className={"py-7 text-center text-[12.5px] text-cr-slate-60"}>
              {t("KlaraDashboard_UpdateCenter_Empty")}
            </p>
          ) : (
            <SessionList
              entries={entries}
              loadedPages={data?.pages.length ?? 0}
              hasNextPage={hasNextPage}
              isFetching={isFetching}
              fetchNextPage={handleFetchNextPage}
              selectedEntryId={selectedEntryId}
              scrollToSelected={!selection.isScrolled}
              onScrolledToSelected={handleScrolledToSelected}
              jump={jumpBounds}
            />
          )}
        </CardState>
      </div>
    </Drawer>
  )
}

const TotalInfoButton = React.forwardRef<HTMLButtonElement, TTooltipButtonProps>(
  ({open: _open, close: _close, ...props}, ref) => {
    const {t} = useTranslation()
    const descriptionId = React.useId()

    // The explanation is also the button's description, so it does not depend on the tooltip being open.
    return (
      <>
        <button
          ref={ref}
          type={"button"}
          aria-label={t("KlaraDashboard_UpdateCenter_TotalInfoLabel")}
          aria-describedby={descriptionId}
          className={"flex rounded-full p-1 text-cr-slate-40 hover:text-cr-blue"}
          {...props}
        >
          <InformationCircleIcon className={"size-4"} aria-hidden={"true"} />
        </button>
        <span id={descriptionId} className={"sr-only"}>
          {t("KlaraDashboard_UpdateCenter_TotalInfo")}
        </span>
      </>
    )
  }
)
TotalInfoButton.displayName = "TotalInfoButton"
